// setup-generator-modal.tsx — first-run AI starter kit.
// Profile form → AI proposes a tailored set → user toggles items → create.
// Reuses FILE_PLAN_SCHEMA / normalizeBatchItems / addEntitiesBatch.
import { useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";
import { useEscapeKey } from "../lib/use-escape-key";
import { createAiClient } from "../lib/ai/ai-runtime";
import { AiError } from "../lib/ai/ai-client";
import type { FilePlanResult } from "../lib/ai/file-extract";
import { buildSetupGenRequest, SETUP_AREAS, type SetupProfile } from "../lib/ai/setup-generate";
import { normalizeBatchItems, kindLabel, type BatchItem } from "../lib/ai/assistant-batch-create";
import { todayIso } from "../lib/date";
import type { LifeOsApi } from "../lib/life-os-contract";

function errText(err: unknown): string {
  if (err instanceof AiError) return err.message;
  return err instanceof Error && err.message ? err.message : "Không tạo được gợi ý. Thử lại nhé.";
}

export function SetupGeneratorModal({ api, onClose }: { api: LifeOsApi; onClose: () => void }) {
  const [step, setStep] = useState<"form" | "loading" | "preview" | "error">("form");
  const [role, setRole] = useState("");
  const [focus, setFocus] = useState("");
  const [areas, setAreas] = useState<string[]>([]);
  const [items, setItems] = useState<BatchItem[]>([]);
  const [included, setIncluded] = useState<boolean[]>([]);
  const [error, setError] = useState("");
  useEscapeKey(onClose);

  const toggleArea = (a: string) =>
    setAreas((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  const generate = async () => {
    if (!role.trim() && !focus.trim() && areas.length === 0) return;
    setStep("loading");
    setError("");
    try {
      const profile: SetupProfile = { role: role.trim(), focus: focus.trim(), areas };
      const res = await createAiClient().generateJson<FilePlanResult>(buildSetupGenRequest(profile, todayIso()));
      const got = normalizeBatchItems(res?.items);
      if (got.length === 0) { setError("AI chưa đề xuất được mục nào. Bạn thử mô tả rõ hơn."); setStep("error"); return; }
      setItems(got);
      setIncluded(got.map(() => true));
      setStep("preview");
    } catch (err) {
      setError(errText(err));
      setStep("error");
    }
  };

  const apply = () => {
    const chosen = items.filter((_, i) => included[i]);
    if (chosen.length === 0) return;
    api.actions.addEntitiesBatch(chosen);
    onClose();
  };

  const chosenCount = included.filter(Boolean).length;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-ink-900/50 p-4 backdrop-blur-sm" onClick={onClose}>
      <div role="dialog" aria-modal="true" className="fade-in flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-ink-200 px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 text-white"><Sparkles className="h-5 w-5" /></span>
            <div>
              <h2 className="font-display text-[16px] font-bold text-ink-900">Khởi tạo bằng AI</h2>
              <p className="text-[12px] text-ink-500">Điền vài thông tin, AI dựng bộ khởi đầu cho bạn.</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Đóng" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-500 hover:bg-ink-200/60"><X className="h-4 w-4" /></button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {(step === "form" || step === "error") && (
            <div className="space-y-3">
              {step === "error" && <p className="rounded-xl bg-rose-50 px-3 py-2 text-[12.5px] font-medium text-rose-700">{error}</p>}
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-ink-500">Vai trò / công việc của bạn</span>
                <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="vd: Lập trình viên, Sinh viên, Quản lý dự án…" className="w-full rounded-xl border border-ink-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-500" />
              </label>
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-ink-500">Mục tiêu / điều muốn cải thiện</span>
                <textarea value={focus} onChange={(e) => setFocus(e.target.value)} rows={2} placeholder="vd: cân bằng công việc – sức khoẻ, hoàn thành đồ án, tiết kiệm…" className="w-full resize-none rounded-xl border border-ink-200 px-3 py-2.5 text-[14px] outline-none focus:border-brand-500" />
              </label>
              <div>
                <span className="mb-1.5 block text-[12px] font-semibold text-ink-500">Lĩnh vực quan tâm</span>
                <div className="flex flex-wrap gap-1.5">
                  {SETUP_AREAS.map((a) => (
                    <button key={a} type="button" onClick={() => toggleArea(a)} className={`rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${areas.includes(a) ? "bg-brand-500 text-white" : "bg-ink-200/50 text-ink-600 hover:bg-ink-200"}`}>{a}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === "loading" && (
            <div className="flex flex-col items-center justify-center gap-3 py-12 text-ink-500">
              <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
              <p className="text-[13px] font-semibold">AI đang dựng bộ khởi đầu cho bạn…</p>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-2">
              <p className="text-[12px] text-ink-500">Bỏ chọn mục không cần. Chi tiết có thể chỉnh trong app sau khi tạo.</p>
              {items.map((it, i) => (
                <label key={i} className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-ink-200 p-2.5">
                  <input type="checkbox" checked={included[i]} onChange={() => setIncluded((p) => p.map((v, j) => (j === i ? !v : v)))} className="mt-0.5 h-4 w-4 accent-brand-500" />
                  <span className="min-w-0">
                    <span className="mr-1.5 rounded bg-brand-100 px-1.5 py-0.5 text-[10px] font-bold text-brand-600">{kindLabel(it.kind)}</span>
                    <span className="text-[13.5px] font-semibold text-ink-800">{it.title}</span>
                    {(it.projectName || it.goalName) && <span className="block text-[11px] text-ink-500">→ {it.projectName ? `dự án "${it.projectName}"` : `mục tiêu "${it.goalName}"`}</span>}
                    {it.subtasks?.length ? <span className="block text-[11px] text-ink-400">{it.subtasks.length} việc con</span> : null}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-ink-200 px-5 py-3">
          {step === "preview" ? (
            <>
              <button onClick={() => setStep("form")} className="rounded-xl border border-ink-200 px-3 py-2 text-[13px] font-bold text-ink-700 hover:bg-ink-100">Tạo lại</button>
              <button onClick={apply} disabled={chosenCount === 0} className="gbrand rounded-xl px-4 py-2 text-[13px] font-bold text-white disabled:opacity-50">Tạo {chosenCount} mục</button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="rounded-xl border border-ink-200 px-3 py-2 text-[13px] font-bold text-ink-700 hover:bg-ink-100">Bỏ qua</button>
              <button onClick={() => void generate()} disabled={step === "loading" || (!role.trim() && !focus.trim() && areas.length === 0)} className="gbrand flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-bold text-white disabled:opacity-50">
                {step === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Tạo gợi ý
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
